<template>
  <section
    v-if="sidebarComponents.length === 0"
    class="layout-pt-lg layout-pb-lg"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div class="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-6 tw-px-6">
      <div class="tw-flex tw-w-full tw-flex-row tw-justify-center">
        <h2 :class="isSmall ? 'heading-medium tw-text-center tw-text-3xl' : 'heading-medium'">
          {{ vars.titleText }}
        </h2>
      </div>

      <div class="tw-flex tw-w-full tw-flex-col tw-gap-8">
        <div class="tw-relative tw-w-full">
          <div class="tw-px-6 md:tw-px-16">
            <Swiper
              @swiper="onSwiper"
              @slideChange="onSlideChange"
              :pagination="false"
              :slides-per-view="3"
              :space-between="24"
              :clickable="true"
              :centered-slides="true"
              :breakpoints="{
                320: { slidesPerView: 1, spaceBetween: 20, centeredSlides: true },
                768: { slidesPerView: 2, spaceBetween: 24, centeredSlides: false },
                1024: { slidesPerView: 3, spaceBetween: 24, centeredSlides: false },
              }"
              class="tw-w-full"
            >
              <SwiperSlide
                v-for="(guide, index) in guides"
                :key="`ourguide-guide-${index}`"
                class="!tw-flex !tw-h-auto !tw-flex-col"
              >
                <div
                  class="n-card tw-group tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-[380px] tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-[var(--c-rounded)] tw-text-start tw-transition-all tw-duration-500"
                  @click.prevent="handleCardClick(guide)"
                  style="border-radius: var(--rounded); min-height: 480px"
                >
                  <div class="tw-relative tw-h-full tw-w-full tw-overflow-hidden">
                    <library-image
                      v-if="guide.image"
                      :src="guide.image"
                      :is-ratio-free="true"
                      image-class="tw-h-full tw-w-full tw-object-cover tw-object-center tw-transition-transform tw-duration-500 group-hover:tw-scale-110"
                      style="border-radius: var(--rounded); object-fit: scale-down"
                    />
                  </div>
                </div>

                <div class="tw-mx-auto tw-flex tw-w-full tw-max-w-[380px] tw-flex-col" style="height: 150px">
                  <div class="tw-flex-grow" @click.prevent="handleCardClick(guide)">
                    <h3
                      class="tw-mb-2 tw-mt-4 tw-line-clamp-2 tw-cursor-pointer tw-overflow-hidden tw-uppercase tw-text-[#333333]"
                    >
                      {{ guide.title }}
                    </h3>
                  </div>

                  <div class="tw-flex">
                    <nuxt-link
                      @click.stop="handleCardClick(guide)"
                      :class="['tw-w-auto tw-cursor-pointer', `n-${guide.button_type}`]"
                    >
                      {{ guide.button_label }}
                    </nuxt-link>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          <!-- Desktop prev arrow -->
          <div
            class="arrow-button tw-absolute tw-left-2 tw-top-1/2 tw-z-10 tw-hidden tw--translate-y-1/2 tw-transform tw-cursor-pointer tw-select-none md:tw-block"
            @click="slidePrev"
            v-if="canPaginateBack"
          >
            <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.4697 14.0303C10.1768 13.7374 10.1768 13.2626 10.4697 12.9697L15.2426 8.1967C15.5355 7.90381 16.0104 7.90381 16.3033 8.1967C16.5962 8.48959 16.5962 8.96447 16.3033 9.25736L12.0607 13.5L16.3033 17.7426C16.5962 18.0355 16.5962 18.5104 16.3033 18.8033C16.0104 19.0962 15.5355 19.0962 15.2426 18.8033L10.4697 14.0303ZM61 14.25H11V12.75H61V14.25Z"
                fill="#BFA87E"
              />
              <circle
                cx="13.5"
                cy="13.5"
                r="13"
                transform="matrix(-1 0 0 1 27 0)"
                stroke="#BFA87E"
                stroke-opacity="0.5"
              />
            </svg>
          </div>
          <!-- Desktop next arrow -->
          <div
            class="arrow-button tw-absolute tw-right-2 tw-top-1/2 tw-z-10 tw-hidden tw--translate-y-1/2 tw-transform tw-cursor-pointer tw-select-none md:tw-block"
            @click="slideNext"
            v-if="canPaginateNext"
          >
            <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M50.5303 14.0303C50.8232 13.7374 50.8232 13.2626 50.5303 12.9697L45.7574 8.1967C45.4645 7.90381 44.9896 7.90381 44.6967 8.1967C44.4038 8.48959 44.4038 8.96447 44.6967 9.25736L48.9393 13.5L44.6967 17.7426C44.4038 18.0355 44.4038 18.5104 44.6967 18.8033C44.9896 19.0962 45.4645 19.0962 45.7574 18.8033L50.5303 14.0303ZM0 14.25H50V12.75H0V14.25Z"
                fill="#BFA87E"
              />
              <circle cx="47.5" cy="13.5" r="13" stroke="#BFA87E" stroke-opacity="0.5" />
            </svg>
          </div>
        </div>

        <!-- Mobile arrows -->
        <div class="tw-mt-2 tw-flex tw-items-center tw-justify-center tw-gap-4 md:tw-hidden">
          <div v-if="canPaginateBack" @click="slidePrev" class="arrow-button">
            <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.4697 14.0303C10.1768 13.7374 10.1768 13.2626 10.4697 12.9697L15.2426 8.1967C15.5355 7.90381 16.0104 7.90381 16.3033 8.1967C16.5962 8.48959 16.5962 8.96447 16.3033 9.25736L12.0607 13.5L16.3033 17.7426C16.5962 18.0355 16.5962 18.5104 16.3033 18.8033C16.0104 19.0962 15.5355 19.0962 15.2426 18.8033L10.4697 14.0303ZM61 14.25H11V12.75H61V14.25Z"
                fill="#BFA87E"
              />
              <circle
                cx="13.5"
                cy="13.5"
                r="13"
                transform="matrix(-1 0 0 1 27 0)"
                stroke="#BFA87E"
                stroke-opacity="0.5"
              />
            </svg>
          </div>
          <div v-if="canPaginateNext" @click="slideNext" class="arrow-button">
            <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M50.5303 14.0303C50.8232 13.7374 50.8232 13.2626 50.5303 12.9697L45.7574 8.1967C45.4645 7.90381 44.9896 7.90381 44.6967 8.1967C44.4038 8.48959 44.4038 8.96447 44.6967 9.25736L48.9393 13.5L44.6967 17.7426C44.4038 18.0355 44.4038 18.5104 44.6967 18.8033C44.9896 19.0962 45.4645 19.0962 45.7574 18.8033L50.5303 14.0303ZM0 14.25H50V12.75H0V14.25Z"
                fill="#BFA87E"
              />
              <circle cx="47.5" cy="13.5" r="13" stroke="#BFA87E" stroke-opacity="0.5" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <popup v-if="selectedCard && popupConfig.isActive" :config="popupConfig" custom-width="90%">
      <template #content>
        <library-form v-if="selectedCard.formData" :form="selectedCard.formData" version="v2" :component="component" />
      </template>
    </popup>
  </section>

  <div v-else-if="sidebarComponents.length > 0" :style="`background-color: var(--c-${vars.backgroundColor})`">
    <div
      class="tw-grid tw-w-full tw-grid-cols-12 tw-grid-rows-1 tw-items-center tw-p-7"
      :style="`color:white`"
      style="border-radius: var(--rounded)"
    >
      <h3 class="tw-col-span-12 tw-mb-4">{{ vars.titleText }}</h3>
      <div class="swiper-wrapper tw-col-span-12">
        <Swiper
          :key="'guides-swiper-' + guides.length"
          :modules="[SwiperPagination]"
          :pagination="false"
          :slides-per-view="1"
          :space-between="10"
          effect="fade"
          :clickable="true"
          :watch-overflow="true"
          @slideChange="onSlideChange"
          @swiper="onSwiper"
          :breakpoints="{
            320: { slidesPerView: 1, spaceBetween: 10 },
          }"
          class="tw-w-full"
        >
          <SwiperSlide
            v-for="(guide, index) in guides"
            :key="`ourguide-guide-${index}`"
            class="!tw-flex !tw-h-auto !tw-flex-col"
          >
            <div
              class="n-card tw-group tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-[380px] tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-[var(--c-rounded)] tw-text-start tw-transition-all tw-duration-500"
              @click.prevent="handleCardClick(guide)"
              style="border-radius: var(--rounded); min-height: 480px"
            >
              <div class="tw-relative tw-h-full tw-w-full tw-overflow-hidden">
                <library-image
                  v-if="guide.image"
                  :src="guide.image"
                  :is-ratio-free="true"
                  image-class="tw-h-full tw-w-full tw-object-cover tw-object-center tw-transition-transform tw-duration-500 group-hover:tw-scale-110"
                  style="border-radius: var(--rounded); object-fit: scale-down"
                />
              </div>
            </div>

            <div class="tw-mx-auto tw-flex tw-w-full tw-max-w-[380px] tw-flex-col" style="height: 150px">
              <div class="tw-flex-grow" @click.prevent="handleCardClick(guide)">
                <h3
                  class="tw-mb-2 tw-mt-4 tw-line-clamp-2 tw-cursor-pointer tw-overflow-hidden tw-uppercase tw-text-white"
                >
                  {{ guide.title }}
                  <br />
                  {{ guide.title }}
                </h3>
              </div>

              <div class="tw-flex">
                <nuxt-link
                  @click.stop="handleCardClick(guide)"
                  :class="['tw-w-auto tw-cursor-pointer', `n-${guide.button_type}`]"
                >
                  {{ guide.button_label }}
                </nuxt-link>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
      <div class="tw-col-span-12 tw-mt-6 tw-flex tw-items-center tw-justify-center tw-gap-4">
        <div v-if="canPaginateBack" @click="swiper.slidePrev()" class="arrow-button tw-cursor-pointer">
          <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.4697 14.0303C10.1768 13.7374 10.1768 13.2626 10.4697 12.9697L15.2426 8.1967C15.5355 7.90381 16.0104 7.90381 16.3033 8.1967C16.5962 8.48959 16.5962 8.96447 16.3033 9.25736L12.0607 13.5L16.3033 17.7426C16.5962 18.0355 16.5962 18.5104 16.3033 18.8033C16.0104 19.0962 15.5355 19.0962 15.2426 18.8033L10.4697 14.0303ZM61 14.25H11V12.75H61V14.25Z"
              fill="#BFA87E"
            />
            <circle
              cx="13.5"
              cy="13.5"
              r="13"
              transform="matrix(-1 0 0 1 27 0)"
              stroke="#BFA87E"
              stroke-opacity="0.5"
            />
          </svg>
        </div>
        <div v-if="canPaginateNext" @click="swiper.slideNext()" class="arrow-button tw-cursor-pointer">
          <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M50.5303 14.0303C50.8232 13.7374 50.8232 13.2626 50.5303 12.9697L45.7574 8.1967C45.4645 7.90381 44.9896 7.90381 44.6967 8.1967C44.4038 8.48959 44.4038 8.96447 44.6967 9.25736L48.9393 13.5L44.6967 17.7426C44.4038 18.0355 44.4038 18.5104 44.6967 18.8033C44.9896 19.0962 45.4645 19.0962 45.7574 18.8033L50.5303 14.0303ZM0 14.25H50V12.75H0V14.25Z"
              fill="#BFA87E"
            />
            <circle cx="47.5" cy="13.5" r="13" stroke="#BFA87E" stroke-opacity="0.5" />
          </svg>
        </div>
      </div>
    </div>

    <popup v-if="selectedCard && popupConfig.isActive" :config="popupConfig" custom-width="90%">
      <template #content>
        <library-form v-if="selectedCard.formData" :form="selectedCard.formData" version="v2" :component="component" />
      </template>
    </popup>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { ComponentMixin } from '~/mixins/component.mixin';
import { Pagination as SwiperPagination } from 'swiper/modules';
import Popup from '~/components/common/popup/Popup.vue';
import { useWebsiteStore } from '~/units/website/store';

export default defineNuxtComponent({
  name: 'OurGuide',
  mixins: [ComponentMixin],
  components: { Popup },
  data() {
    return {
      swiper: null,
      canPaginateBack: true,
      canPaginateNext: true,
      selectedCard: null,
      popupConfig: { isActive: false },
      swiperModules: [SwiperPagination],
    };
  },
  mounted() {
    this.onSlideChange();
  },
  computed: {
    ...mapState(useWebsiteStore, ['sidebarComponents']),
    guides() {
      return this.groupedVariables.guide;
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
    isSmall() {
      return this.vars.isSmallNumber;
    },
  },
  methods: {
    onSlideChange() {
      if (!this.swiper) return;
      this.canPaginateBack = !this.swiper.isBeginning;
      this.canPaginateNext = !this.swiper.isEnd;
    },
    onSwiper(swiper) {
      this.swiper = swiper;
    },
    slidePrev() {
      this.swiper?.slidePrev();
    },
    slideNext() {
      this.swiper?.slideNext();
    },
    handleCardClick(guide: any) {
      this.selectedCard = {
        ...guide,
        formData: this.parseFormV2(guide.guide || guide.form),
      };
      this.popupConfig.isActive = true;
    },
  },
});
</script>

<style scoped>
.arrow-button svg circle {
  transition: stroke-opacity 0.3s ease;
}
.arrow-button:hover svg circle {
  stroke-opacity: 1;
}
.arrow-button {
  cursor: pointer;
}
</style>
